import styled from "@emotion/styled";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Stack,
  Typography
} from "@mui/material";
import moment from "moment";
import { forwardRef, useState } from "react";

const StyledBackgroundInputField = styled(MuiTextField)(
  ({ theme, neutral, rounded }) => ({
    "& .MuiInputBase-root": {
      borderRadius: rounded ? "4rem" : "0.5rem",
      backgroundColor: `${
        neutral ? theme.palette.neutral.main : theme.palette.secondary.main
      }`,
      "&.Mui-disabled": {
        backgroundColor: neutral
          ? theme.palette.neutral.light || "#f5f5f5"
          : theme.palette.secondary.light || "#e0e0e0",
        color: theme.palette.text.disabled || "#9e9e9e",
        "& fieldset": {
          borderColor: theme.palette.grey[300] || "#d1d5db"
        },
        "& .MuiInputBase-input": {
          WebkitTextFillColor: theme.palette.text.disabled || "#9e9e9e",
          cursor: "not-allowed"
        }
      }
    },
    "& .MuiInputBase-input": {
      padding: "8px 16px"
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: `none`
    },
    "&.Mui-focused": {
      "& .MuiOutlinedInput-notchedOutline": {
        border: `none`
      }
    }
  })
);

const StyledBorderedInputField = styled(MuiTextField)(({ theme, neutral }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "0.5rem",
    "& fieldset": {
      borderWidth: "2px",
      borderStyle: "solid",
      borderColor: `${theme.palette.primary.main}`
    },
    "&:hover fieldset": {
      borderColor: `${theme.palette.primary.main}`
    },
    "&.Mui-focused fieldset": {
      borderColor: `${theme.palette.primary.main}`
    },
    "&.Mui-disabled": {
      backgroundColor: neutral
        ? theme.palette.neutral.light || "#f5f5f5"
        : theme.palette.grey[100] || "#f5f5f5",
      "& fieldset": {
        borderColor: theme.palette.grey[300] || "#d1d5db"
      },
      "& .MuiInputBase-input": {
        WebkitTextFillColor: theme.palette.text.disabled || "#9e9e9e"
      }
    }
  },
  "& .MuiInputBase-input": {
    padding: "8px 16px"
  }
}));

export const TextField = forwardRef(
  (
    {
      label,
      isLabelBold,
      hasNoBg,
      isPlain,
      neutral,
      name,
      register = () => {},
      width = "100%",
      required,
      disablePastDates,
      disableFutureDates,
      ...rest
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);

    return (
      <Stack width={width}>
        {!!label && (
          <Typography
            variant="subtitle2"
            component="label"
            htmlFor={name}
            sx={{
              fontWeight: isLabelBold && 700,
              "&:after": {
                content: required ? '" *"' : '""',
                color: "red"
              }
            }}
          >
            {label}
          </Typography>
        )}
        {hasNoBg ? (
          <StyledBorderedInputField
            id={name}
            name={name}
            size="small"
            neutral={neutral}
            variant={isPlain && "standard"}
            inputProps={{
              "aria-label": label,
              ...(disablePastDates && {
                min: moment().format("YYYY-MM-DD")
              }),
              ...(disableFutureDates && {
                max: moment().format("YYYY-MM-DD")
              })
            }}
            InputProps={{
              type:
                rest.type === "password"
                  ? showPassword
                    ? "text"
                    : "password"
                  : rest.type,
              endAdornment:
                rest.type === "password" ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {!showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ) : null
            }}
            {...register(name)}
            {...rest}
            inputRef={ref}
          />
        ) : (
          <StyledBackgroundInputField
            id={name}
            name={name}
            size="small"
            neutral={neutral}
            inputProps={{
              "aria-label": label,
              ...(disablePastDates && {
                min: moment().format("YYYY-MM-DD")
              }),
              ...(disableFutureDates && {
                max: moment().format("YYYY-MM-DD")
              })
            }}
            InputProps={{
              type:
                rest.type === "password"
                  ? showPassword
                    ? "text"
                    : "password"
                  : rest.type,
              endAdornment:
                rest.type === "password" ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {!showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ) : null
            }}
            type={
              rest.type === "password"
                ? showPassword
                  ? "text"
                  : "password"
                : rest.type
            }
            {...register(name)}
            {...rest}
            inputRef={ref}
          />
        )}
      </Stack>
    );
  }
);

TextField.displayName = "TextField";
